import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import {isPendingSession, PENDING_SESSION_ID, type CommandsResponse} from '../types/session'
import {SessionContext, useSessionContext} from '../contexts/SessionContext'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {useQueryClient} from '@github-ui/react-query'
import {useAgentSessionsAlive, type AgentSessionAlivePayload} from './use-agent-sessions-alive'
import {use, useCallback} from 'react'
import {useDebounce} from '@github-ui/use-debounce'

export interface UseListCommandsProps {
  pollingInterval: number
}

interface UseSessionCommandsQueryProps {
  sessionId: string | undefined
  enabled?: boolean
  refetchInterval?: number | false
  copilotApiUrl?: string
}

export function generateSessionCommandsQueryKey(sessionId: string | undefined, copilotApiUrl?: string) {
  const key = ['agent-session-commands', sessionId ?? '']
  return copilotApiUrl ? [...key, copilotApiUrl] : key
}

export function useSessionCommandsQuery({
  sessionId,
  enabled = true,
  refetchInterval = false,
  copilotApiUrl,
}: UseSessionCommandsQueryProps) {
  const queryClient = useQueryClient()
  const sessionContext = use(SessionContext)
  const effectiveCopilotApiUrl = copilotApiUrl ?? sessionContext?.copilotApiUrl
  const queryKey = generateSessionCommandsQueryKey(sessionId, effectiveCopilotApiUrl)

  return useCAPIQuery<CommandsResponse>({
    queryKey,
    queryFn: async ({queryKey: currentQueryKey}) => {
      if (!sessionId) throw new Error('No session ID provided')

      const result = await fetchCAPI({
        path: `/agents/sessions/${sessionId}/commands?sort=created_at,asc`,
        method: 'GET',
        ...(effectiveCopilotApiUrl ? {copilotApiUrl: effectiveCopilotApiUrl} : {}),
      })

      if (!result.ok) {
        throw new Error(`Failed to fetch commands: ${result.statusText}`)
      }

      const serverData = (await result.json()) as CommandsResponse

      // Get current cache data to preserve optimistic commands
      const currentData = queryClient.getQueryData<CommandsResponse>(currentQueryKey)

      if (currentData) {
        const optimisticCommands = currentData.commands.filter(cmd => cmd.id.startsWith('optimistic-'))

        // Merge server commands with optimistic commands (optimistic commands go at the end since they're the newest)
        return {
          commands: [...serverData.commands, ...optimisticCommands],
        }
      }

      return serverData
    },
    refetchInterval,
    enabled: enabled && !!sessionId && sessionId !== PENDING_SESSION_ID,
  })
}

export function useListCommands({pollingInterval}: UseListCommandsProps) {
  const {session, copilotApiUrl} = useSessionContext()
  const activeSessionId = session.id
  const queryClient = useQueryClient()

  const handleAliveUpdate = useCallback(
    (payload?: AgentSessionAlivePayload) => {
      // Only invalidate commands for the active session
      if (payload?.session_id && payload.session_id !== activeSessionId) return
      queryClient.invalidateQueries({
        queryKey: generateSessionCommandsQueryKey(activeSessionId, copilotApiUrl),
      })
    },
    [queryClient, activeSessionId, copilotApiUrl],
  )

  const debouncedHandleAliveUpdate = useDebounce(handleAliveUpdate, pollingInterval, {maxWait: 5000})

  // Subscribe to alive updates and get connection status for polling fallback
  const {isConnected: aliveIsConnected} = useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: true,
  })

  // Only poll when session is active AND alive is not connected
  const shouldPoll =
    !aliveIsConnected && (session.state === 'in_progress' || session.state === 'queued') ? pollingInterval : false

  const query = useSessionCommandsQuery({
    sessionId: activeSessionId,
    enabled: !isPendingSession(session),
    refetchInterval: shouldPoll,
    copilotApiUrl,
  })

  return {
    data: query.data?.commands ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}
